import { useCallback } from "react";
import * as identify from "utils/treeUtils";
import type { AnyFolder, Node } from "interfaces/schemas";
import { useTranslate } from "hooks/useTranslate";

interface GenNewFolderNameReturn {
  data: string | null;
  error: Error | null;
}

export const useGenNewFolderName = () => {
  const translate = useTranslate();

  const genNewFolderName = useCallback(
    (
      copiedNodeFolderName: string,
      siblings: Node[] = [],
      isNewLayer: boolean = false,
    ): GenNewFolderNameReturn => {
      const nodeFolderName = isNewLayer
        ? translate("PROJECT_NEW_NODE")
        : copiedNodeFolderName;

      if (typeof nodeFolderName !== "string") {
        const error = new Error("Failed to generate new folder name");
        return { error, data: null };
      }

      const regExpNodeNumber = /\((\d+)\)$/;
      const arrOfSameFolderNames: string[] = [];
      if (siblings && siblings.length) {
        siblings.forEach((siblingNode) => {
          if (!identify.isAnyFolder(siblingNode)) {
            return;
          }
          const { folderName } = siblingNode as AnyFolder;
          if (folderName === nodeFolderName) {
            arrOfSameFolderNames.push(folderName);
          }
        });
      }

      // Case: New layer-node
      if (!siblings || !siblings.length) {
        return { data: nodeFolderName, error: null };
      }

      // Case: New node in a layer or there's not nodes with the same folderName
      if (!arrOfSameFolderNames.length) {
        return { data: nodeFolderName, error: null };
      }

      const arrOfFolderNameNumbers: number[] = [];
      siblings.forEach((siblingNode: Node) => {
        if (identify.isAnyFolder(siblingNode)) {
          if (!identify.isAnyFolder(siblingNode)) {
            return;
          }
          const { folderName } = siblingNode as AnyFolder;
          const matchedFolderNameNumber =
            folderName.match(regExpNodeNumber)?.[1];
          if (matchedFolderNameNumber) {
            arrOfFolderNameNumbers.push(Number(matchedFolderNameNumber));
          }
        }
      });

      // Case: Only one node with same folderNames "folderName (1)"
      if (
        !regExpNodeNumber.test(nodeFolderName) &&
        !arrOfFolderNameNumbers.length
      ) {
        return { data: `${nodeFolderName} (1)`, error: null };
      }

      const changedNumberFolderName = nodeFolderName
        .replace(regExpNodeNumber, "")
        .trim();

      // Case: Node(s) with same folderNames and with number. "folderName (n+1)"
      return {
        data: `${changedNumberFolderName} (${Math.max(...arrOfFolderNameNumbers) + 1})`,
        error: null,
      };
    },
    [translate],
  );

  return genNewFolderName;
};
